import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '@nextui-org/react';
import CtySpinner from '../spinner/CtySpinner';
import {
  coverageCellClass,
  formatCurrency,
  formatCustomerName,
  formatDate,
  getContractCoveredSurchargeDescriptions,
  type ContractData,
} from './claimIntakeUtils';
import { buildPoliciesFromContract, buildProductFromContract } from './buildUnifiedClaimPayload';
import type { ClaimIntakeSession } from './claimIntakeAgentApi';
import {
  fetchPreviousClaimsViaDifyStream,
  hasPreviousClaimsReviewContent,
  INITIAL_PREVIOUS_CLAIMS_STATE,
  readPreviousClaimsFetchError,
  type PreviousClaimsState,
  type PreviousClaimRow,
} from './previousClaimsApi';

const previousClaimsFetchInFlight = new Set<string>();

export type PreviousClaimsFetchConfig = Readonly<{
  contractNumber: string;
  session: ClaimIntakeSession | null;
  token: string | null | undefined;
  sourceId: string;
  agentsBaseUrl?: string;
  aiworkerUsageId?: string | null;
}>;

export function ReadOnlyGrid({
  fields,
}: Readonly<{ fields: { id: string; label: string; value: string }[] }>) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 w-full">
      {fields.map((field) => (
        <div key={field.id} className={coverageCellClass}>
          <p className="text-xs text-default-400 shrink-0">{field.label}</p>
          <div className="flex items-start gap-1.5 min-h-0 flex-1 overflow-hidden">
            <p className="text-sm font-semibold text-foreground break-words overflow-y-auto min-h-0 flex-1 leading-snug">
              {field.value || '—'}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

const autoChip = (
  <Chip size="sm" color="secondary" variant="flat" className="uppercase text-[10px] font-bold bg-green-100 text-green-700 px-2">
    AUTO-POPULATED
  </Chip>
);

export function ContractSurchargesSection({ contract }: Readonly<{ contract: ContractData }>) {
  const surcharges = getContractCoveredSurchargeDescriptions(contract);
  if (surcharges.length === 0) return null;

  return (
    <div className="mt-6 pt-6 border-t border-default-200 sm:col-span-2">
      <h4 className="text-sm font-semibold text-foreground mb-3">Contract Surcharges</h4>
      <div className="flex flex-wrap gap-2">
        {surcharges.map((description) => (
          <Chip key={description} size="sm" variant="flat" color="primary" className="text-xs">
            {description}
          </Chip>
        ))}
      </div>
    </div>
  );
}

export function buildVehicleClaimsPolicyFields(contract: ContractData) {
  const policy = buildPoliciesFromContract(contract);
  return [
    { id: 'policy_coverage', label: 'Coverage', value: String(policy.policy_coverage) },
    { id: 'policy_term', label: 'Term (months)', value: String(policy.policy_term) },
    { id: 'policy_inservice_mileage', label: 'In-Service Mileage', value: String(policy.policy_inservice_mileage) },
    // { id: 'policy_max_mileage', label: 'Max Mileage', value: String(policy.policy_max_mileage) },
    { id: 'policy_sale_date', label: 'Sale Date', value: formatDate(String(policy.policy_sale_date)) },
    { id: 'policy_sale_odometer', label: 'Sale Odometer', value: String(policy.policy_sale_odometer) },
  ];
}

export function buildVehicleClaimsProductFields(contract: ContractData) {
  const product = buildProductFromContract(contract);
  const year = product.product_year;
  const yearDisplay = year != null && Number.isFinite(year) ? String(year) : '';

  return [
    { id: 'product_year', label: 'Year', value: yearDisplay },
    { id: 'product_name', label: 'Make', value: String(product.product_name) },
    { id: 'product_model', label: 'Model', value: String(product.product_model) },
    { id: 'product_serial_no', label: 'Serial No (VIN)', value: String(product.product_serial_no) },
  ];
}

export function ContractLookupReviewSection({ contract }: Readonly<{ contract: ContractData | null }>) {
  if (!contract) {
    return <p className="text-sm text-default-500">No contract loaded.</p>;
  }

  const deductible = contract.coverage?.terms?.[0]?.deductible?.amount;
  const rows = [
    { key: 'cn', label: 'Contract Number', text: contract.contractNumber },
    { key: 'holder', label: 'Contract Holder', text: formatCustomerName(contract.customer) },
    { key: 'cov', label: 'Coverage Plan', text: contract.coverage?.description || '—' },
    // { key: 'eff', label: 'Effective Date', text: formatDate(contract.effectiveDate) },
    // { key: 'exp', label: 'Expiration Date', text: formatDate(contract.expirationDate) },
    { key: 'vin', label: 'VIN', text: contract.vehicle?.vin },
    {
      key: 'pt',
      label: 'Product Type',
      text: contract.coverage?.productType?.code || contract.coverage?.productType?.description || '—',
    },
  ];

  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
      <div className="mb-1">
        <h3 className="text-base sm:text-lg font-semibold text-foreground">Contract Details</h3>
        <p className="text-sm text-default-500">Auto-populated from contract lookup. All fields are read-only.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4 mt-6">
        <div>
          <p className="text-xs text-default-400 mb-0.5">{rows[0]?.label}</p>
          <p className="text-sm font-semibold text-foreground">{rows[0]?.text}</p>
        </div>
        <div>
          <p className="text-xs text-default-400 mb-0.5">Status</p>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{contract.status?.description}</span>
            {contract.status?.code === 'A' ? (
              <Chip size="sm" color="success" variant="flat" className="text-[10px] font-bold">
                ACTIVE
              </Chip>
            ) : null}
          </div>
        </div>
        {rows.slice(1).map((row) => (
          <div key={row.key}>
            <p className="text-xs text-default-400 mb-0.5">{row.label}</p>
            <p className="text-sm font-semibold text-foreground">{row.text}</p>
          </div>
        ))}
        <div>
          <p className="text-xs text-default-400 mb-1">Deductible</p>
          <p className="text-xl font-bold text-foreground">{formatCurrency(deductible)}</p>
        </div>       
        <ContractSurchargesSection contract={contract} />
      </div>
    </div>
  );
}

export function PolicyReadOnlySection({ contract }: Readonly<{ contract: ContractData }>) {
  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6 w-full">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
        <h2 className="text-base sm:text-lg font-semibold text-foreground">Policy</h2>
        {autoChip}
      </div>
      <p className="text-sm text-default-500 mb-5">Policy details extracted from the warranty contract.</p>
      <ReadOnlyGrid fields={buildVehicleClaimsPolicyFields(contract)} />
    </div>
  );
}

export function ProductReadOnlySection({ contract }: Readonly<{ contract: ContractData }>) {
  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6 w-full">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
        <h2 className="text-base sm:text-lg font-semibold text-foreground">Product</h2>
        {autoChip}
      </div>
      <p className="text-sm text-default-500 mb-5">Vehicle product details from the warranty contract.</p>
      <ReadOnlyGrid fields={buildVehicleClaimsProductFields(contract)} />
    </div>
  );
}

export function PreviousClaimsSection({
  claims,
  loading,
  error,
}: Readonly<{
  claims: readonly PreviousClaimRow[];
  loading: boolean;
  error: string | null;
}>) {
  const { t } = useTranslation();

  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6 w-full">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
        <h2 className="text-base sm:text-lg font-semibold text-foreground">{t('Previous Claims')}</h2>
        {autoChip}
      </div>
      <p className="text-sm text-default-500 mb-5">
        {t('Prior claims associated with this contract.')}
      </p>

      {loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-default-500">
          <CtySpinner size="sm" />
          <span>{t('Loading previous claims…')}</span>
        </div>
      ) : null}

      {!loading && error ? (
        <p className="text-sm text-danger py-2">{error}</p>
      ) : null}

      {!loading && !error && claims.length === 0 ? (
        <p className="text-sm text-default-500 py-2">{t('No previous claims found for this contract.')}</p>
      ) : null}

      {!loading && !error && claims.length > 0 ? (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[320px] text-sm">
            <thead>
              <tr className="border-b border-default-200 text-left">
                <th className="py-2 px-2 text-xs font-semibold text-default-500 uppercase tracking-wide">
                  {t('Claim Number')}
                </th>
                <th className="py-2 px-2 text-xs font-semibold text-default-500 uppercase tracking-wide">
                  {t('Status')}
                </th>
                <th className="py-2 px-2 text-xs font-semibold text-default-500 uppercase tracking-wide">
                  {t('Claim Date')}
                </th>
              </tr>
            </thead>
            <tbody>
              {claims.map((row) => (
                <tr key={row.claimNumber} className="border-b border-default-200 last:border-b-0">
                  <td className="py-2.5 px-2 font-semibold text-foreground">{row.claimNumber}</td>
                  <td className="py-2.5 px-2 text-default-700">{row.status}</td>
                  <td className="py-2.5 px-2 text-default-700 whitespace-nowrap">{row.claimDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export function PreviousClaimsLoader({
  fetchConfig,
  state,
  onStateChange,
}: Readonly<{
  fetchConfig: PreviousClaimsFetchConfig;
  state: PreviousClaimsState;
  onStateChange: Dispatch<SetStateAction<PreviousClaimsState>>;
}>) {
  const trimmedContract = fetchConfig.contractNumber.trim();

  useEffect(() => {
    if (!trimmedContract) return undefined;
    if (state.loadedForContract === trimmedContract) return undefined;
    if (previousClaimsFetchInFlight.has(trimmedContract)) return undefined;

    const usageId = fetchConfig.aiworkerUsageId?.trim();
    const bearer = fetchConfig.token?.trim();
    if (!fetchConfig.session || !bearer || !fetchConfig.sourceId || !usageId) return undefined;

    previousClaimsFetchInFlight.add(trimmedContract);
    onStateChange((prev) => ({ ...prev, loading: true, error: null }));

    void fetchPreviousClaimsViaDifyStream({
      session: fetchConfig.session,
      token: bearer,
      baseUrl: fetchConfig.agentsBaseUrl,
      sourceId: fetchConfig.sourceId,
      contractNumber: trimmedContract,
      aiworkerUsageId: usageId,
    })
      .then((rows) => {
        onStateChange({
          claims: rows,
          loading: false,
          error: null,
          loadedForContract: trimmedContract,
        });
      })
      .catch((err: unknown) => {
        onStateChange({
          claims: [],
          loading: false,
          error: readPreviousClaimsFetchError(err),
          loadedForContract: trimmedContract,
        });
      })
      .finally(() => {
        previousClaimsFetchInFlight.delete(trimmedContract);
      });
  }, [
    trimmedContract,
    fetchConfig.session,
    fetchConfig.token,
    fetchConfig.sourceId,
    fetchConfig.agentsBaseUrl,
    fetchConfig.aiworkerUsageId,
    state.loadedForContract,
    onStateChange,
  ]);

  return (
    <PreviousClaimsSection
      claims={state.claims}
      loading={state.loading}
      error={state.error}
    />
  );
}

export function VehicleClaimsReviewSections({
  contract,
  previousClaims,
}: Readonly<{
  contract: ContractData | null;
  previousClaims?: PreviousClaimsState;
}>) {
  if (!contract) {
    return <p className="text-sm text-default-500">No contract loaded for vehicle and claims.</p>;
  }

  const showPreviousClaims = hasPreviousClaimsReviewContent(previousClaims ?? INITIAL_PREVIOUS_CLAIMS_STATE, contract.contractNumber);

  return (
    <div className="space-y-4 sm:space-y-6">
      <PolicyReadOnlySection contract={contract} />
      <ProductReadOnlySection contract={contract} />
      {showPreviousClaims && previousClaims ? (
        <PreviousClaimsSection
          claims={previousClaims.claims}
          loading={false}
          error={previousClaims.error}
        />
      ) : null}
    </div>
  );
}
